import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import CustomCalendar from "../../components/CustomCalendar";
import Navbar from "../../components/Navbar";
import "./AppointmentDetail.css";
import moment from "moment-timezone";
import { FiUser, FiMail, FiPhone, FiCalendar, FiClock, FiTag, FiUserCheck } from "react-icons/fi";

const AppointmentDetail = () => {
  const { appointmentId } = useParams();
  const navigate = useNavigate();
  const [verificationResult, setVerificationResult] = useState(null);
  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  const [newDate, setNewDate] = useState("");
  const [_newTime, _setNewTime] = useState("");
  const [selectedTime, setSelectedTime] = useState("");

  const API_BASE = import.meta.env.VITE_APP_BASE_API;
  const API_VERSION = import.meta.env.VITE_APP_API_VERSION;

  // Use the patient-specific endpoint instead of the admin endpoint
  const API_ENDPOINT = `${API_BASE}${API_VERSION}/appointment/patient/get`;

  // Date helper: robust, timezone-aware formatting
  const convertToHumanReadable = (input, tz) => {
    try {
      const zone = tz || "Asia/Kolkata";
      const m = moment(input).tz(zone);
      if (!m.isValid()) return String(input ?? "");
      // Example: Aug 21, 2025 • 1:58 PM
      return m.format("MMM DD, YYYY • h:mm A");
    } catch {
      const d = new Date(input);
      if (isNaN(d.getTime())) return String(input ?? "");
      return d.toLocaleString();
    }
  };

  const formatRawScheduled = (appointment) => {
    if (!appointment) return "";
    const rawDate = appointment.appointment_date || "";
    const [datePart = "", timePartRaw = ""] = rawDate.split("T");
    const [year, month, day] = datePart.split("-");
    const MONTHS = [
      "Jan",
      "Feb",
      "Mar",
      "Apr",
      "May",
      "Jun",
      "Jul",
      "Aug",
      "Sep",
      "Oct",
      "Nov",
      "Dec",
    ];
    const monthIndex = month ? Number(month) - 1 : -1;
    const monthLabel = MONTHS[monthIndex] || month || "";
    const dayLabel = day ? day.padStart(2, "0") : "";
    const timeDisplay = (appointment.appointment_time || timePartRaw.replace(/Z$/, "")).trim();
    if (!datePart && !timeDisplay) return "";
    const dateSegment = [monthLabel, dayLabel, year].filter(Boolean).join(" ");
    return dateSegment && timeDisplay ? `${dateSegment} • ${timeDisplay}` : dateSegment || timeDisplay;
  };


  const formatDateForInput = (dateString) => {
    if (!dateString) return { date: "", time: "" };
    const date = new Date(dateString);
    return {
      date: date.toISOString().split("T")[0], // Format: "YYYY-MM-DD"
      time: date.toTimeString().slice(0, 5), // Format: "HH:MM"
    };
  };

  // Robust timestamp parsing (supports ISO strings and legacy compact formats)
  const _parseTimestampToDate = (ts) => {
    if (!ts) return null;
    if (ts instanceof Date) return ts;
    if (typeof ts === "number") return new Date(ts);
    if (typeof ts === "string") {
      const s = ts.trim();
      if (/^\d{12,14}$/.test(s)) {
        const year = parseInt(s.slice(0, 4));
        const month = parseInt(s.slice(4, 6)) - 1;
        const day = parseInt(s.slice(6, 8));
        const hours = parseInt(s.slice(8, 10));
        const minutes = parseInt(s.slice(10, 12));
        const seconds = s.length >= 14 ? parseInt(s.slice(12, 14)) : 0;
        return new Date(year, month, day, hours, minutes, seconds);
      }
      const parsed = Date.parse(s);
      if (!isNaN(parsed)) return new Date(parsed);
    }
    return null;
  };

  // Format date as YYYY-MM-DDTHH:mm:ss.SSS±HH:MM
  const _formatISOWithOffset = (date) => {
    if (!date) return "";
    const pad = (n, w = 2) => String(n).padStart(w, "0");
    const y = date.getFullYear();
    const m = pad(date.getMonth() + 1);
    const d = pad(date.getDate());
    const hh = pad(date.getHours());
    const mm = pad(date.getMinutes());
    const ss = pad(date.getSeconds());
    const ms = String(date.getMilliseconds()).padStart(3, "0");
    const tz = -date.getTimezoneOffset();
    const sign = tz >= 0 ? "+" : "-";
    const tzh = pad(Math.floor(Math.abs(tz) / 60));
    const tzm = pad(Math.abs(tz) % 60);
    return `${y}-${m}-${d}T${hh}:${mm}:${ss}.${ms}${sign}${tzh}:${tzm}`;
  };

  useEffect(() => {
    const verifyAppointment = async () => {
      setIsLoading(true);
      setError(null);
      setVerificationResult(null);

      if (!appointmentId) {
        setError("Appointment ID is missing.");
        setIsLoading(false);
        return;
      }

      try {
        // Get token from localStorage
        const token = localStorage.getItem("token");

        if (!token) {
          setError("Authentication required. Please login.");
          navigate("/login", {
            state: { from: `/appointment/verify/${appointmentId}` },
          });
          return;
        }

        console.log(`Fetching appointment details for ID: ${appointmentId}`);

        // Make the request with proper authentication
        const response = await fetch(`${API_ENDPOINT}/${appointmentId}`, {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          credentials: "include",
        });

        if (!response.ok) {
          if (response.status === 401) {
            setError("Authentication required. Please login.");
            setTimeout(() => {
              navigate("/login", {
                state: { from: `/appointment/verify/${appointmentId}` },
              });
            }, 2000);
            return;
          }

          if (response.status === 403) {
            setError(
              "You are not authorized to view this appointment. You can only view appointments associated with your email."
            );
            setTimeout(() => {
              navigate("/appointments");
            }, 3000);
            return;
          }

          const errorData = await response.json();
          throw new Error(
            errorData?.message || `HTTP error! status: ${response.status}`
          );
        }

        const data = await response.json();
        if (data.success) {
          setVerificationResult(data.appointment);
          const { date, time: _time } = formatDateForInput(
            data.appointment?.appointment_date
          );
          setNewDate(date);
        } else {
          throw new Error(data.message || "Failed to load appointment details");
        }
      } catch (err) {
        console.error("Error fetching appointment:", err);
        const message = err && err.message ? err.message : "Failed to load appointment details";
        setError(message);
      } finally {
        setIsLoading(false);
      }
    };

    verifyAppointment();
  }, [appointmentId, API_ENDPOINT, navigate]);

  const handleUpdate = async (status) => {
    try {
      const token = localStorage.getItem("token");
      if (!token) {
        setError("Authentication required. Please login.");
        return;
      }

      // Make sure we have a valid ID to use
      const idToUse = verificationResult?.meetingId;
      if (!idToUse) {
        throw new Error("Invalid appointment identifier");
      }

      console.log("Updating appointment with ID:", idToUse);
      console.log("New status:", status);

      const response = await fetch(
        `${API_BASE}${API_VERSION}/appointment/patient/update/${idToUse}`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          credentials: "include",
          body: JSON.stringify({
            status: status,
          }),
        }
      );

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || "Failed to update appointment");
      }

      if (data.success) {
        toast.success(`Appointment ${status.toLowerCase()} successfully`);
        setVerificationResult(data.appointment);
      } else {
        throw new Error(data.message || "Failed to update appointment");
      }
    } catch (err) {
      console.error("Error updating appointment:", err);
      setError(err.message);
      toast.error(err.message || "Failed to update appointment");
    }
  };

  // Convert 12-hour format to 24-hour format
  const _convertTo24Hour = (time12h) => {
    const [time, modifier] = time12h.split(" ");
    let [hours, minutes] = time.split(":");

    if (hours === "12") {
      hours = "00";
    }

    if (modifier === "pm") {
      hours = parseInt(hours, 10) + 12;
    }

    return `${hours.toString().padStart(2, "0")}:${minutes}`;
  };

  const handleSaveEdit = async () => {
    try {
      const token = localStorage.getItem("token");
      if (!token) {
        setError("Authentication required. Please login.");
        return;
      }

      // Convert selected time to PST
      const selectedMoment = moment
        .tz(
          `${newDate} ${selectedTime}`,
          "YYYY-MM-DD hh:mm A",
          moment.tz.guess()
        )
        .tz("America/Los_Angeles");

      const selectedDate = selectedMoment.toDate();

      // Use meetingId instead of _id
      const idToUse = verificationResult.meetingId || appointmentId;

      const response = await fetch(
        `${API_BASE}${API_VERSION}/appointment/patient/update/${idToUse}`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          credentials: "include",
          body: JSON.stringify({
            appointment_date: selectedDate.toISOString(),
          }),
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(
          errorData?.message || `HTTP error! status: ${response.status}`
        );
      }

      const data = await response.json();
      if (data.success) {
        toast.success("Appointment date and time updated successfully");
        setVerificationResult(data.appointment);
        setIsEditing(false);
      } else {
        throw new Error(data.message || "Failed to update appointment date");
      }
    } catch (err) {
      console.error("Error updating appointment date:", err);
      setError(err.message);
      toast.error(err.message);
    }
  };

  if (isLoading) {
    return (
      <>
        <Navbar />
        <div className="flex items-center justify-center h-[calc(100vh-64px)]">
          <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-blue-500"></div>
        </div>
      </>
    );
  }

  return (
    <>
      <Navbar />
      <div className="min-h-screen ">
        <div className="container mx-auto px-4 mb-20 ">
          {verificationResult && (
            <>
              <div
                className={`text-4xl text-center pt-10 mb-4 ${
                  getStatusIcon(verificationResult.status).color
                }`}
              >
                {getStatusIcon(verificationResult.status).icon} { verificationResult.status}
              </div>
            </>
          )}
          <div className="max-w-md mx-auto bg-white p-6 rounded-2xl shadow-lg">
            {error && (
              <div className="text-red-500 font-semibold mb-4">{error}</div>
            )}

            {verificationResult && (
              <>
                <div className="mt-4 space-y-4">

                  {/* Appointment card */}
                  <div className="overflow-hidden rounded-2xl border border-gray-100 bg-white">
                    <div className="flex items-center gap-2 border-b border-gray-100 px-4 py-3">
                      <FiCalendar className="h-4 w-4 text-gray-500" />
                      <h3 className="text-sm font-semibold text-gray-700">Appointment</h3>
                    </div>
                    <div className="grid grid-cols-1 gap-4 p-4 sm:grid-cols-2">
                    
                      <div>
                        <div className="text-xs uppercase tracking-wide text-gray-500">Name</div>
                        <div className="mt-0.5 text-gray-900 flex items-center gap-2">
                          <FiUserCheck className="h-4 w-4 text-gray-400" />
                          <span>{verificationResult.firstName} {verificationResult.lastName}</span>
                        </div>
                      </div>

                    {verificationResult.appointment_type && (
                      <div>
                        <div className="text-xs uppercase tracking-wide text-gray-500">Type</div>
                        <div className="mt-0.5 text-gray-900 flex items-center gap-2">
                          <FiTag className="h-4 w-4 text-gray-400" />
                          <span>{verificationResult.appointment_type}</span>
                        </div>
                      </div>
                    )}

                      <div>
                        <div className="text-xs uppercase tracking-wide text-gray-500">Scheduled</div>
                        <div className="mt-0.5 text-gray-900 flex items-center gap-2">
                          <FiCalendar className="h-4 w-4 text-gray-400" />
                          <span>{formatRawScheduled(verificationResult)}</span>
                        </div>
                      </div>
                      <div>
                        <div className="text-xs uppercase tracking-wide text-gray-500">Created</div>
                        <div className="mt-0.5 text-gray-900 flex items-center gap-2">
                          <FiClock className="h-4 w-4 text-gray-400" />
                          <span>
                            {convertToHumanReadable(
                              verificationResult.createdAt,
                              "Asia/Kolkata"
                            )}
                          </span>
                        </div>
                      </div>
                      <div>
                        <div className="text-xs uppercase tracking-wide text-gray-500">Status</div>
                        <div className="mt-0.5">
                          <span
                            className={`inline-flex items-center rounded-full px-2 py-1 text-xs font-medium
                              ${verificationResult.status === "Accepted" ? "bg-green-50 text-green-700 ring-1 ring-green-600/10" :
                              verificationResult.status === "Pending" ? "bg-yellow-50 text-yellow-700 ring-1 ring-yellow-600/10" :
                              (verificationResult.status === "Cancelled" || verificationResult.status === "Rejected") ? "bg-red-50 text-red-700 ring-1 ring-red-600/10" :
                              "bg-gray-50 text-gray-700 ring-1 ring-gray-600/10"}`}
                          >
                            {verificationResult.status}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </>
            )}

            {!error && verificationResult && (
              <div className="mt-6 space-y-3">
                {!isEditing ? (
                  <>
                    {/* Only show action buttons if the appointment is not in a final state */}
                    {verificationResult.status !== "Cancelled" &&
                      verificationResult.status !== "Rejected" && (
                        <>
                          <button
                            onClick={() => handleUpdate("Accepted")}
                            disabled={verificationResult.status === "Accepted"}
                            className="w-full px-4 py-2 bg-blue-500 text-white rounded-full shadow hover:bg-blue-600 disabled:bg-gray-400"
                          >
                            Confirm
                          </button>
                          <button
                            onClick={() => setIsEditing(true)}
                            className="w-full px-4 py-2 bg-yellow-500 text-white rounded-full shadow hover:bg-yellow-600"
                          >
                            Reschedule
                          </button>
                          <button
                            onClick={() => handleUpdate("Cancelled")}
                            className="w-full px-4 py-2 bg-red-500 text-white rounded-full shadow hover:bg-red-600"
                          >
                            Cancel Appointment
                          </button>
                        </>
                      )}
                    {/* Always show a back button */}
                    <button
                      onClick={() => navigate("/appointments")}
                      className="w-full px-4 py-2 bg-gray-500 text-white rounded-full shadow hover:bg-gray-600"
                    >
                      Back to Appointments
                    </button>
                  </>
                ) : (
                  <div>
                    <div className="mb-4">
                      <CustomCalendar
                        selectedDate={newDate ? new Date(newDate) : new Date()}
                        onDateSelect={(date) =>
                          setNewDate(date.toISOString().split("T")[0])
                        }
                        doctorName={`${
                          verificationResult?.doctor?.firstName || ""
                        } ${verificationResult?.doctor?.lastName || ""}`}
                        doctorImage={verificationResult?.doctor?.profileImage}
                        onTimeSelect={setSelectedTime}
                        selectedTime={selectedTime}
                      />
                    </div>
                    <div className="flex mt-3 gap-2">
                      <button
                        onClick={handleSaveEdit}
                        className="flex-1 px-4 py-2 bg-green-500 text-white rounded-md shadow hover:bg-green-600"
                      >
                        Save
                      </button>
                      <button
                        onClick={() => setIsEditing(false)}
                        className="flex-1 px-4 py-2 bg-gray-500 text-white rounded-md shadow hover:bg-gray-600"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

// Helper functions for status display
function getStatusIcon(status) {
  switch (status) {
    case "Accepted":
      return { icon: "✔", color: "text-green-500" };
    case "Cancelled":
    case "Rejected":
      return { icon: "✖", color: "text-red-500" };
    case "Pending":
      return { icon: "⏳", color: "text-yellow-500" };
    default:
      return { icon: "❓", color: "text-gray-500" };
  }
}

function _getStatusColor(status) {
  switch (status) {
    case "Accepted":
      return "text-green-600";
    case "Cancelled":
    case "Rejected":
      return "text-red-600";
    case "Pending":
      return "text-yellow-600";
    default:
      return "text-gray-600";
  }
}

function _getStatusText(status) {
  switch (status) {
    case "Accepted":
      return "Appointment Confirmed";
    case "Cancelled":
    case "Rejected":
      return "Appointment Cancelled";
    case "Pending":
      return "Appointment Pending";
    default:
      return "Appointment Status Unknown";
  }
}

export default AppointmentDetail;
